const DB_NAME = 'nodecharm';
const DB_VERSION = 2;
const STORE_NAME = '=L7(gH+K1;`3';
const STORAGE_LIMIT_BYTES = 250 * 1024 * 1024; // 250 MB

export class StorageLimitError extends Error {
    constructor(usedMB, limitMB) {
        super(`Storage limit exceeded: ${usedMB}MB / ${limitMB}MB`);
        this.name = 'StorageLimitError';
        this.usedMB = usedMB;
        this.limitMB = limitMB;
    }
}

function initializeDB() {
    return new Promise((resolve, reject) => {
        if (!window.indexedDB) {
            console.error("Your browser doesn't support IndexedDB.");
            return;
        }

        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (event) => {
            console.error('Database error:', event.target.error);
            reject(event.target.error);
        };

        request.onsuccess = (event) => {
            resolve(event.target.result);
        };

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'key' });
            }
        };
    });
}

function deleteDB() {
    return new Promise((resolve, reject) => {
        const request = indexedDB.deleteDatabase(DB_NAME);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
    });
}

/**
 * Run a callback with a fresh IndexedDB connection, then close it.
 * Prevents iOS Safari stale/blocked connection issues.
 * If the operation fails (e.g. missing object store), deletes the DB,
 * recreates it, and retries once.
 */
async function withDb(callback) {
    const db = await initializeDB();
    try {
        return await callback(db);
    } catch (err) {
        db.close();
        console.warn('[IndexedDB] Operation failed, resetting database:', err);
        await deleteDB();
        const freshDb = await initializeDB();
        try {
            return await callback(freshDb);
        } finally {
            freshDb.close();
        }
    } finally {
        db.close();
    }
}

export async function addOrUpdateGraph(graph, key) {
    graph.key = key;

    if (navigator.storage && navigator.storage.estimate) {
        const { usage } = await navigator.storage.estimate();
        if (usage && usage >= STORAGE_LIMIT_BYTES) {
            const usedMB = Math.round(usage / (1024 * 1024));
            const limitMB = Math.round(STORAGE_LIMIT_BYTES / (1024 * 1024));
            throw new StorageLimitError(usedMB, limitMB);
        }
    }

    return withDb((db) => {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.put(graph);
            request.onsuccess = () => resolve();
            request.onerror = (event) => {
                console.error('Add or update graph error:', event.target.errorCode);
                reject(event.target.errorCode);
            };
        });
    });
}

export function deleteTopic(topic) {
    return new Promise(async (resolve, reject) => {
        const confirmed = window.confirm('Are you sure you want to delete this topic?');
        if (!confirmed) {
            reject('Deletion canceled');
            return;
        }

        try {
            await withDb((db) => {
                return new Promise((res, rej) => {
                    const transaction = db.transaction([STORE_NAME], 'readwrite');
                    const store = transaction.objectStore(STORE_NAME);
                    const request = store.delete(topic);
                    request.onsuccess = () => res();
                    request.onerror = (event) => {
                        console.error('Delete topic error:', event.target.errorCode);
                        rej(event.target.errorCode);
                    };
                });
            });
            resolve();
        } catch (err) {
            reject(err);
        }
    });
}

/** Delete by key without confirm (e.g. for file graph cleanup). */
export function deleteByKey(key) {
    return withDb((db) => {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.delete(key);
            request.onsuccess = () => resolve();
            request.onerror = (event) => {
                console.error('Delete by key error:', event.target.errorCode);
                reject(event.target.errorCode);
            };
        });
    });
}

export function getFromIndexedDB(name) {
    return withDb((db) => {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.get(name);
            request.onsuccess = (event) => {
                resolve(event.target.result || undefined);
            };
            request.onerror = (event) => {
                console.error('Get Topic error:', event.target.errorCode);
                reject(event.target.errorCode);
            };
        });
    });
}

export function getAllTopics() {
    return withDb((db) => {
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.getAll();
            request.onsuccess = (event) => resolve(event.target.result);
            request.onerror = (event) => {
                console.error('Get all topics error:', event.target.errorCode);
                reject(event.target.errorCode);
            };
        });
    });
}
